import { useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { TERMS_VERSION } from '../terms';
import DisclaimerScreen from './DisclaimerScreen';

// Wraps the app and re-shows the disclaimer to already-onboarded users whose
// accepted terms version is out of date (or never recorded). First-time
// acceptance is still handled inside CreateProfile during onboarding — this only
// catches existing users after the terms change.
export default function TermsGate({ children }) {
  const { user, profile, loading, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(false);

  // Don't gate while auth is resolving, for logged-out users, or for users still
  // mid-onboarding (no complete profile yet).
  const needsReaccept =
    !loading &&
    !!user &&
    profile?.profile_complete &&
    profile.terms_version !== TERMS_VERSION;

  if (!needsReaccept) return children;

  const handleReaccept = async () => {
    setSaving(true);
    try {
      // terms_accepted_at / terms_version are server-pinned (C2) — a direct
      // update is silently reverted by the profiles guard. accept_terms() is
      // the one path that may write them.
      const { error } = await supabase.rpc('accept_terms', { p_version: TERMS_VERSION });
      if (error) throw error;
      await refreshProfile(); // profile.terms_version now matches → gate drops
    } catch (err) {
      console.error('Failed to record updated terms acceptance:', err);
      setSaving(false); // let them retry
    }
  };

  return (
    <DisclaimerScreen
      title="Updated Terms & Disclaimer"
      subtitle="We've updated our Terms & Disclaimer. Please review and accept to keep using Nomad."
      agreeLabel="I Agree"
      busy={saving}
      onAgree={handleReaccept}
    />
  );
}
